import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ImagesApi, type Brand } from '@/lib/admin-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/**
 * ImageAttach — คอมโพเนนต์กลาง "แนบรูป" ใช้ซ้ำทุกเมนู (FAQ, Reply Templates, ฯลฯ)
 *  - เลือกจากคลังรูป (product_images) ที่ตั้งค่าไว้แล้ว
 *  - หรือวางลิงก์ใหม่ → พรีวิวสด + บันทึกเข้าคลังรูปอัตโนมัติ (ใช้ซ้ำได้ทุกที่)
 *  ค่า value = imageUrl | null ; onChange ส่งกลับ url หรือ null
 */
export function ImageAttach({
  brand,
  value,
  onChange,
  defaultColor,
}: {
  brand: Brand;
  value: string | null;
  onChange: (url: string | null) => void;
  /** สีที่เลือกในคำตอบ — ใช้กรองคลังรูปให้อัตโนมัติ */
  defaultColor?: string;
}) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<null | 'lib' | 'new'>(null);
  const [newUrl, setNewUrl] = useState('');
  const [colorFilter, setColorFilter] = useState<string>(defaultColor ?? '');

  const imagesQ = useQuery({
    queryKey: ['product-images', brand],
    queryFn: () => ImagesApi.list({ brand }),
    enabled: mode === 'lib',
  });

  // วางลิงก์ใหม่ → บันทึกเข้าคลังรูปอัตโนมัติ แล้ว set ค่า
  const saveNewMut = useMutation({
    mutationFn: (url: string) =>
      ImagesApi.create({ brand, imageUrl: url, context: 'main', active: true, sortOrder: 0 }),
    onSuccess: (_r, url) => {
      qc.invalidateQueries({ queryKey: ['product-images', brand] });
      onChange(url);
      setNewUrl('');
      setMode(null);
      toast.success('แนบรูปแล้ว + บันทึกเข้าคลังรูปอัตโนมัติ');
    },
    onError: (e) => toast.error(`ผิดพลาด: ${e.message}`),
  });

  // dedupe คลังรูปตาม URL
  const seen = new Set<string>();
  const allLib = (imagesQ.data ?? []).filter((img) => {
    if (!img.imageUrl || seen.has(img.imageUrl)) return false;
    seen.add(img.imageUrl);
    return true;
  });
  // ตัวเลือกสีที่มีในคลัง (ให้กรองรูปตามสี)
  const colors = Array.from(new Set(allLib.map((i) => i.color).filter(Boolean) as string[]));
  const lib = colorFilter ? allLib.filter((i) => i.color === colorFilter) : allLib;

  return (
    <div className="space-y-2">
      <label className="text-xs text-muted-foreground font-semibold flex items-center gap-1">
        🖼️ รูปแนบ (บอทจะส่งรูปนี้พร้อมคำตอบ — เว้นว่าง = ไม่ส่งรูป)
      </label>

      {/* รูปที่แนบอยู่ */}
      {value ? (
        <div className="flex items-center gap-2">
          <img
            src={value}
            alt="attached"
            className="w-20 h-20 object-cover rounded border border-border bg-muted"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.opacity = '0.25'; }}
          />
          <div className="flex flex-col gap-1">
            <span className="text-[11px] text-green-700">✅ แนบรูปแล้ว</span>
            <button
              type="button"
              className="text-[11px] text-red-600 hover:underline text-left"
              onClick={() => onChange(null)}
            >
              ลบรูปออก
            </button>
            <button
              type="button"
              className="text-[11px] text-primary hover:underline text-left"
              onClick={() => setMode(mode ? null : 'lib')}
            >
              เปลี่ยนรูป
            </button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <Button type="button" size="sm" variant={mode === 'lib' ? 'default' : 'outline'} className="h-8" onClick={() => setMode(mode === 'lib' ? null : 'lib')}>
            📎 เลือกจากคลังรูป
          </Button>
          <Button type="button" size="sm" variant={mode === 'new' ? 'default' : 'outline'} className="h-8" onClick={() => setMode(mode === 'new' ? null : 'new')}>
            🔗 วางลิงก์ใหม่
          </Button>
        </div>
      )}

      {/* โหมดเลือกจากคลัง */}
      {mode === 'lib' && (
        <div className="border border-border rounded-lg bg-card p-2">
          {/* กรองตามสี */}
          {colors.length > 0 && (
            <div className="flex flex-wrap items-center gap-1 mb-2 text-[11px]">
              <span className="text-muted-foreground">กรองสี:</span>
              <button
                type="button"
                className={`px-2 py-0.5 rounded-full border ${colorFilter === '' ? 'bg-primary text-primary-foreground border-primary' : 'border-border'}`}
                onClick={() => setColorFilter('')}
              >ทั้งหมด</button>
              {colors.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`px-2 py-0.5 rounded-full border ${colorFilter === c ? 'bg-primary text-primary-foreground border-primary' : 'border-border'}`}
                  onClick={() => setColorFilter(c)}
                >{c}</button>
              ))}
            </div>
          )}
          {imagesQ.isLoading ? (
            <div className="text-xs text-muted-foreground">กำลังโหลดคลังรูป…</div>
          ) : lib.length === 0 ? (
            <div className="text-xs text-muted-foreground">ไม่มีรูป{colorFilter ? `สี ${colorFilter}` : ''}ในคลัง — ใช้ "วางลิงก์ใหม่" หรือไปเพิ่มที่หน้ารูปสินค้า</div>
          ) : (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {lib.map((img) => (
                <button
                  key={img.id}
                  type="button"
                  title={`${img.altText || img.imageUrl}${img.color ? ` · สี ${img.color}` : ''}`}
                  onClick={() => { onChange(img.imageUrl); setMode(null); }}
                  className="shrink-0 rounded border-2 border-border hover:border-primary overflow-hidden w-16 h-16"
                >
                  <img
                    src={img.imageUrl}
                    alt={img.altText ?? ''}
                    className="w-full h-full object-cover"
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.opacity = '0.25'; }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* โหมดวางลิงก์ใหม่ */}
      {mode === 'new' && (
        <div className="border border-border rounded-lg bg-card p-2 space-y-2">
          <Input
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            placeholder="วางลิงก์รูป (https://...) — จะบันทึกเข้าคลังให้อัตโนมัติ"
            className="h-8 text-xs"
          />
          {newUrl.trim() && (
            <img
              src={newUrl.trim()}
              alt="preview"
              className="w-20 h-20 object-cover rounded border border-border bg-muted"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.opacity = '0.25'; }}
            />
          )}
          <Button
            type="button"
            size="sm"
            className="h-8"
            disabled={!newUrl.trim() || saveNewMut.isPending}
            onClick={() => saveNewMut.mutate(newUrl.trim())}
          >
            ใช้รูปนี้ + เก็บเข้าคลัง
          </Button>
        </div>
      )}
    </div>
  );
}
